import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listFindingsPageForRun, type FindingsListQuery } from '@/api/findings';
import type { FindingOut } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import {
  QUEUE_PAGE_SIZE,
  appendPage,
  changedSinceLoad,
  emptyPagerState,
  nextFetchOffset,
  shouldFetchMore,
  type PagerState,
} from '@/lib/findingsPager';

interface QueueState {
  key: string;
  pager: PagerState;
  rows: Map<string, FindingOut>;
}

const EMPTY_ROWS: ReadonlyMap<string, FindingOut> = new Map();

/** Server-cursor-driven card source for the flashcard review queue
 *  (INP-778): fetches `order=review` pages on demand, keeps membership
 *  locked per `queueKey` (the filter key — a change resets the queue),
 *  and corrects the next page's offset for dispositions via the pure
 *  pager math in `lib/findingsPager.ts`.
 *
 *  `liveById` (the run's react-query-fresh findings) supplies each
 *  card's current fields and the post-disposition statuses the offset
 *  correction reads; the page-fetched row is the fallback for anything
 *  not in it. `total` is the first page's server total — locked so the
 *  "N of M" indicator doesn't shrink as the practitioner dispositions.
 */
export function useServerFindingQueue({
  runId,
  enabled,
  query,
  queueKey,
  liveById,
}: {
  runId: string;
  enabled: boolean;
  /** Pushdown query (no limit/offset — the hook pages), or null when disabled. */
  query: FindingsListQuery | null;
  queueKey: string;
  liveById: ReadonlyMap<string, FindingOut>;
}): {
  items: FindingOut[];
  total: number | null;
  loadedCount: number;
  /** True once the first page (or an empty result) has landed. */
  ready: boolean;
  ensureLoadedThrough: (cursorIndex: number) => void;
  /** True after a page fetch failed — auto-fetching pauses until `retry`. */
  error: boolean;
  /** Clear the failure and refetch immediately. */
  retry: () => void;
} {
  const { isSignedIn, getIdToken } = useAuth();
  const [state, setState] = useState<QueueState>(() => ({
    key: queueKey,
    pager: emptyPagerState(),
    rows: new Map(),
  }));
  const inFlight = useRef(false);
  // The queue key a page fetch failed under. Auto-fetching (first-page
  // effect + prefetch) pauses while it matches — without the gate a
  // failing first page refires on every render with no backoff. A key
  // change resets the queue, so a stale error key reads as no error.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const failed = errorKey === queueKey;

  // A stale state (from a previous filter key) reads as empty; the
  // effect below kicks the first fetch for the new key.
  const current = state.key === queueKey ? state : null;
  const pager = current?.pager ?? emptyPagerState();
  const rows = current?.rows ?? EMPTY_ROWS;

  const fetchMore = useCallback(async () => {
    if (!enabled || query === null || !isSignedIn || inFlight.current) return;
    if (pager.exhausted || (pager.total !== null && pager.ids.length >= pager.total)) return;
    inFlight.current = true;
    try {
      const idToken = await getIdToken();
      if (!idToken) return;
      const statusOf = (id: string) => (liveById.get(id) ?? rows.get(id))?.status;
      const offset = nextFetchOffset(pager, changedSinceLoad(pager, statusOf));
      const page = await listFindingsPageForRun(runId, idToken, {
        ...query,
        limit: QUEUE_PAGE_SIZE,
        offset,
      });
      setState((prev) => {
        const base =
          prev.key === queueKey
            ? prev
            : { key: queueKey, pager: emptyPagerState(), rows: new Map<string, FindingOut>() };
        const nextRows = new Map(base.rows);
        for (const item of page.items) {
          if (!nextRows.has(item.finding_id)) nextRows.set(item.finding_id, item);
        }
        return {
          key: queueKey,
          pager: appendPage(base.pager, {
            items: page.items,
            total: page.total,
            nextOffset: page.next_offset,
            offset,
          }),
          rows: nextRows,
        };
      });
    } catch (e) {
      console.error('findings queue page fetch failed', e);
      setErrorKey(queueKey);
    } finally {
      inFlight.current = false;
    }
  }, [enabled, query, isSignedIn, getIdToken, pager, rows, liveById, runId, queueKey]);

  // First page per queue key (also handles the key-change reset: the
  // derived pager reads empty until a fetch lands under the new key).
  useEffect(() => {
    if (enabled && !failed && pager.ids.length === 0 && !pager.exhausted && pager.total === null) {
      void fetchMore();
    }
  }, [enabled, failed, queueKey, pager.ids.length, pager.exhausted, pager.total, fetchMore]);

  const ensureLoadedThrough = useCallback(
    (cursorIndex: number) => {
      if (!failed && shouldFetchMore(pager, cursorIndex)) void fetchMore();
    },
    [failed, pager, fetchMore],
  );

  const retry = useCallback(() => {
    setErrorKey(null);
    // fetchMore itself isn't error-gated (only the auto triggers are),
    // so the refetch fires without waiting for the state to land.
    void fetchMore();
  }, [fetchMore]);

  // Hydrate loaded ids from the freshest row available — the run-level
  // query's copy (updates after every disposition refetch) wins over the
  // page-fetch snapshot.
  const items = useMemo(() => {
    const out: FindingOut[] = [];
    for (const id of pager.ids) {
      const row = liveById.get(id) ?? rows.get(id);
      if (row) out.push(row);
    }
    return out;
  }, [pager.ids, liveById, rows]);

  return {
    items,
    total: pager.total,
    loadedCount: pager.ids.length,
    ready: current !== null && (pager.ids.length > 0 || pager.exhausted),
    ensureLoadedThrough,
    error: failed,
    retry,
  };
}
